var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import useWarningInDev from '@jetshop/core/hooks/useWarningInDev';
import productListQueries from '@jetshop/core/data/queries/ProductLists.gql';
import productListMutations from '@jetshop/core/data/mutations/productListMutations.gql';
import { emptyProductLists, productListMapToArray, PRODUCT_LISTS_KEY } from '.';
import useAuth from '../../components/AuthContext/useAuth';
import { useThunkReducer } from '../useThunkReducer';
import { createListAction, deleteListAction, loginAction, refreshAction } from './action-creators';
import { normalizeServerList } from './list-transforms';
import { init, reducer } from './product-list-reducer';
export const ProductListContext = React.createContext(undefined);
function useMissingQueryWarning(property, queries) {
    return useWarningInDev(!(property in queries), `No \`${property}\` query available in \`queries\` for \`ProductListProvider\`. ` +
        `Check migration guide for 5.11.0 (product lists section) to remove this warning`);
}
export const ProductListProvider = function ProductListProvider(_a) {
    var { queries, initialState = {} } = _a, rest = __rest(_a, ["queries", "initialState"]);
    // NOTE: Warn about missing queries, should be removed in next major release.
    useMissingQueryWarning('all', queries);
    useMissingQueryWarning('createList', queries);
    useMissingQueryWarning('deleteList', queries);
    const { loggedIn } = useAuth();
    const requestIdRef = React.useRef(null);
    const allQuery = queries.all || productListQueries.ProductLists;
    const { data: serverList, client } = useQuery(allQuery, {
        skip: !loggedIn
    });
    const lists = serverList
        ? new Map(serverList.customerProductLists.map(
        // The default list (name === null) must have listId null
        (productList) => [
            productList.name === null ? null : productList.id,
            normalizeServerList(productList)
        ]))
        : emptyProductLists();
    const defaultInitialState = Object.assign({ loggedIn,
        requestIdRef,
        lists }, initialState);
    const [state, dispatch] = useThunkReducer(reducer, defaultInitialState, (initialState) => init(initialState));
    const usingLocalList = !state.loggedIn;
    usePersistLocalList({ lists: state.lists });
    // When localstorage updates, sync it to reducer state
    useLocalStorageListener({ refresh });
    // loginMutation is a mutation to merge product lists
    const [loginMutation] = useMutation(queries.login);
    // To be used to fetch all lists of user after login
    const fetchAll = () => client.query({ query: allQuery });
    const [createMutation] = useMutation(queries.createList || productListMutations.CreateProductList);
    const [deleteListMutation] = useMutation(queries.deleteList || productListMutations.DeleteProductList);
    function deleteList(listId) {
        dispatch(deleteListAction({ listId, deleteListMutation }));
    }
    function createList(name) {
        dispatch(createListAction({ name, createMutation }));
    }
    function refresh() {
        dispatch(refreshAction());
    }
    useEffect(() => {
        if (loggedIn === state.loggedIn)
            return;
        loggedIn
            ? dispatch(loginAction({ loginMutation, fetchAll })) // Merge logged out lists to account
            : dispatch({ type: 'LOGOUT' }); // Clear lists
    }, [dispatch, loggedIn, loginMutation, state.loggedIn]);
    const value = useMemo(() => ({
        createList,
        deleteList,
        state,
        dispatch,
        queries,
        usingLocalList
    }), [dispatch, queries, state, usingLocalList]);
    // We spread props here so during testing we can override value
    return React.createElement(ProductListContext.Provider, Object.assign({ value: value }, rest));
};
/**
 * Persist local list to localStorage on every change
 */
function usePersistLocalList({ lists }) {
    const productListArray = productListMapToArray(lists);
    useEffect(() => {
        localStorage.setItem(PRODUCT_LISTS_KEY, JSON.stringify(productListArray));
    }, [lists]);
}
function useLocalStorageListener({ refresh }) {
    useEffect(() => {
        const listener = (e) => {
            if (e.storageArea === localStorage && e.key === PRODUCT_LISTS_KEY) {
                refresh();
            }
        };
        window.addEventListener('storage', listener);
        return () => {
            window.removeEventListener('storage', listener);
        };
    }, []);
}
//# sourceMappingURL=ProductListContext.js.map